/**
 * Web StickerImageProcessor: reads a picked/pasted image, sniffs animation (core `Stickers.detectAnimation`), and makes
 * the 512×512 sticker: crop (default: centred square) → pica (high-quality resize, lazy chunk) → WebP (PNG where the
 * browser can't encode WebP, e.g. older Safari). Animated originals are uploaded as they are.
 *
 * Background removal isn't offered on the web: the maintained in-browser option (@imgly/background-removal) is
 * AGPL-3.0, outside our permissive-license rule (docs/porting/primitive-gaps.md).
 */
import { ApiFailure, Stickers, type CropArea, type PickedFile, type StickerImageProcessor } from "@bundle/core";
import type { MediaUrls } from "../files/media.ts";

/** GIFs can have their second frame late in the file; static formats say it in the header. */
const SNIFF_BYTES_GIF = 1_200_000;
const SNIFF_BYTES = 65_536;
/** Cap the crop drawn before resizing (memory on phones). */
const MAX_SOURCE_EDGE = 2048;

type PicaInstance = { resize(from: HTMLCanvasElement | ImageBitmap, to: HTMLCanvasElement, options?: { filter?: "lanczos3" | "mks2013" }): Promise<HTMLCanvasElement> };
let picaInstance: Promise<PicaInstance> | undefined;
const loadPica = (): Promise<PicaInstance> => {
  picaInstance ??= import("pica").then((m) => m.default({ features: ["js", "wasm", "cib"] }) as unknown as PicaInstance);
  return picaInstance;
};

function unreadable(): Error {
  return new Error("stickers.create.unreadable");
}

async function bitmapOf(blob: Blob): Promise<ImageBitmap> {
  try {
    return await createImageBitmap(blob);
  } catch {
    throw unreadable();
  }
}

function canvas(width: number, height: number): HTMLCanvasElement {
  const c = document.createElement("canvas");
  c.width = width;
  c.height = height;
  return c;
}

function toBlob(c: HTMLCanvasElement, type: string, quality?: number): Promise<Blob> {
  return new Promise((resolve, reject) => c.toBlob((b) => (b === null ? reject(unreadable()) : resolve(b)), type, quality));
}

/** The crop inside the image (a centred square when absent). */
function clampCrop(crop: CropArea | undefined, width: number, height: number): CropArea {
  if (crop === undefined || crop.width <= 0 || crop.height <= 0) {
    const side = Math.min(width, height);
    return { x: Math.floor((width - side) / 2), y: Math.floor((height - side) / 2), width: side, height: side };
  }
  const x = Math.max(0, Math.min(width - 1, Math.round(crop.x)));
  const y = Math.max(0, Math.min(height - 1, Math.round(crop.y)));
  return { x, y, width: Math.max(1, Math.min(width - x, Math.round(crop.width))), height: Math.max(1, Math.min(height - y, Math.round(crop.height))) };
}

export function createStickerImages(media: MediaUrls): StickerImageProcessor {
  return {
    async inspect(file: PickedFile) {
      const blob = file.source as Blob;
      const isGif = file.mime === "image/gif" || /\.gif$/i.test(file.name);
      const head = new Uint8Array(await blob.slice(0, isGif ? SNIFF_BYTES_GIF : SNIFF_BYTES).arrayBuffer());
      const animated = Stickers.detectAnimation(Array.from(head));
      const bitmap = await bitmapOf(blob);
      const info = { width: bitmap.width, height: bitmap.height, animated, previewUrl: URL.createObjectURL(blob) };
      bitmap.close();
      return info;
    },

    async render(file: PickedFile, options: { crop?: CropArea; removeBackground: boolean }) {
      const bitmap = await bitmapOf(file.source as Blob);
      try {
        const crop = clampCrop(options.crop, bitmap.width, bitmap.height);
        const scale = Math.min(1, MAX_SOURCE_EDGE / Math.max(crop.width, crop.height));
        const src = canvas(Math.max(1, Math.round(crop.width * scale)), Math.max(1, Math.round(crop.height * scale)));
        src.getContext("2d")!.drawImage(bitmap, crop.x, crop.y, crop.width, crop.height, 0, 0, src.width, src.height);
        const size = Stickers.STICKER_SIZE;
        const out = canvas(size, size);
        // Letterbox non-square crops (transparent), keeping the aspect.
        const fit = Math.min(size / src.width, size / src.height);
        const w = Math.max(1, Math.round(src.width * fit));
        const h = Math.max(1, Math.round(src.height * fit));
        const resized = canvas(w, h);
        await (await loadPica()).resize(src, resized, { filter: "mks2013" });
        out.getContext("2d")!.drawImage(resized, Math.floor((size - w) / 2), Math.floor((size - h) / 2));
        let blob = await toBlob(out, "image/webp", 0.9);
        if (blob.type !== "image/webp") blob = await toBlob(out, "image/png");
        const ext = blob.type === "image/webp" ? "webp" : "png";
        const name = `${file.name.replace(/\.[^.]+$/, "") || "sticker"}.${ext}`;
        return { name, mime: blob.type, size: blob.size, lastModified: Date.now(), source: blob };
      } finally {
        bitmap.close();
      }
    },

    supportsBackgroundRemoval: () => false,

    async fromUrl(url: string, name: string, mime: string) {
      const ws = /^\/v1\/w\/([^/?#]+)\//.exec(url)?.[1];
      if (ws !== undefined) await media.ready(decodeURIComponent(ws));
      const resolved = media.resolve(url);
      if (resolved === undefined) throw new ApiFailure("network", 0, "network", "file token unavailable");
      let res: Response;
      try {
        res = await fetch(resolved);
      } catch (e) {
        throw new ApiFailure("network", 0, "network", e instanceof Error ? e.message : "network error");
      }
      if (!res.ok) throw unreadable();
      const blob = await res.blob();
      return { name, mime: blob.type || mime, size: blob.size, lastModified: Date.now(), source: blob };
    },

    release(previewUrl: string) {
      if (previewUrl.startsWith("blob:")) URL.revokeObjectURL(previewUrl);
    },
  };
}
